import { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  RefreshCw, FileText, Receipt, AlertTriangle, Package, Truck, 
  Link2, TrendingUp, DollarSign, Check, X, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const IntegracionPage = () => {
  const { empresaActual } = useApp();
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [costosPendientes, setCostosPendientes] = useState([]);
  const [alertasStock, setAlertasStock] = useState([]);
  const [guiasSinFacturar, setGuiasSinFacturar] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresFinanzas, setProveedoresFinanzas] = useState([]);
  
  // Dialog states
  const [procesarDialogOpen, setProcesarDialogOpen] = useState(false);
  const [costoSeleccionado, setCostoSeleccionado] = useState(null);
  const [tipoProceso, setTipoProceso] = useState('');
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState('');
  
  const [ventaDialogOpen, setVentaDialogOpen] = useState(false);
  const [guiaSeleccionada, setGuiaSeleccionada] = useState(null);
  const [montoVenta, setMontoVenta] = useState('');
  const [clienteNombre, setClienteNombre] = useState('');
  
  // Dialog para vincular proveedor
  const [vincularDialogOpen, setVincularDialogOpen] = useState(false);
  const [personaAVincular, setPersonaAVincular] = useState(null);
  const [proveedorParaVincular, setProveedorParaVincular] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Cargar datos principales
      const [resumenRes, costosRes, alertasRes, guiasRes, provProdRes] = await Promise.all([
        api.get(`/integracion/resumen?empresa_id=${empresaActual?.id || 1}`),
        api.get('/integracion/costos-pendientes'),
        api.get('/integracion/alertas-stock'),
        api.get('/integracion/guias-sin-facturar'),
        api.get('/integracion/proveedores-produccion'),
      ]);
      
      setResumen(resumenRes.data);
      setCostosPendientes(costosRes.data);
      setAlertasStock(alertasRes.data);
      setGuiasSinFacturar(guiasRes.data);
      setProveedores(provProdRes.data);
      
      // Cargar proveedores de finanzas por separado (puede fallar sin afectar el resto)
      try {
        const provFinRes = await api.get('/proveedores', { params: { empresa_id: empresaActual?.id || 1 } });
        setProveedoresFinanzas(provFinRes.data || []);
      } catch (e) {
        console.log('No se pudieron cargar proveedores de finanzas');
        setProveedoresFinanzas([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [empresaActual]);

  const sincronizarCostos = async () => {
    try {
      const res = await api.post('/integracion/costos-pendientes/sincronizar');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al sincronizar');
    }
  };

  const verificarStock = async () => {
    try {
      const res = await api.post('/integracion/alertas-stock/verificar');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al verificar stock');
    }
  };

  const handleProcesarCosto = async () => {
    if (!tipoProceso) {
      toast.error('Selecciona tipo de procesamiento');
      return;
    }
    if (tipoProceso === 'factura' && !proveedorSeleccionado) {
      toast.error('Selecciona un proveedor');
      return;
    }
    
    try {
      await api.post(`/integracion/costos-pendientes/${costoSeleccionado.id}/procesar`, {
        tipo: tipoProceso,
        proveedor_id: tipoProceso === 'factura' ? parseInt(proveedorSeleccionado) : null,
        empresa_id: empresaActual?.id || 1
      });
      toast.success(tipoProceso === 'factura' ? 'Factura creada' : 'Gasto registrado');
      setProcesarDialogOpen(false);
      setCostoSeleccionado(null);
      setTipoProceso('');
      setProveedorSeleccionado('');
      fetchData();
    } catch (error) {
      toast.error('Error al procesar');
    }
  };

  const handleDescartarCosto = async (id) => {
    if (!confirm('¿Descartar este costo?')) return;
    try {
      await api.delete(`/integracion/costos-pendientes/${id}`);
      toast.success('Costo descartado');
      fetchData();
    } catch (error) {
      toast.error('Error al descartar');
    }
  };

  const handleVincularProveedor = async () => {
    if (!proveedorParaVincular) {
      toast.error('Selecciona un proveedor');
      return;
    }
    try {
      await api.post('/integracion/proveedores/vincular', {
        persona_produccion_id: personaAVincular.id,
        proveedor_finanzas_id: parseInt(proveedorParaVincular)
      });
      toast.success(`${personaAVincular.nombre} vinculado correctamente`);
      setVincularDialogOpen(false);
      setPersonaAVincular(null);
      setProveedorParaVincular('');
      fetchData();
    } catch (error) {
      toast.error('Error al vincular');
    }
  };

  const handleCrearProveedorDesdePersona = async (persona) => {
    try {
      await api.post(`/integracion/proveedores/crear-desde-produccion/${persona.id}?empresa_id=${empresaActual?.id || 1}`);
      toast.success(`Proveedor "${persona.nombre}" creado y vinculado`);
      fetchData();
    } catch (error) {
      toast.error('Error al crear proveedor');
    }
  };

  const handleCrearVenta = async () => {
    if (!montoVenta) {
      toast.error('Ingresa el monto');
      return;
    }
    
    try {
      await api.post(`/integracion/guias/${guiaSeleccionada.id}/crear-venta`, {
        guia_id: guiaSeleccionada.id,
        empresa_id: empresaActual?.id || 1,
        cliente_nombre: clienteNombre || 'Cliente General',
        monto: parseFloat(montoVenta)
      });
      toast.success('Venta creada');
      setVentaDialogOpen(false);
      setGuiaSeleccionada(null);
      setMontoVenta('');
      setClienteNombre('');
      fetchData();
    } catch (error) {
      toast.error('Error al crear venta');
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-PE', { 
      style: 'currency', 
      currency: 'PEN' 
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integración Finanzas ↔ Producción</h1>
          <p className="text-gray-500">Sincroniza costos, inventario y ventas entre módulos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sincronizarCostos}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Sincronizar Costos
          </Button>
          <Button variant="outline" onClick={verificarStock}>
            <Package className="w-4 h-4 mr-2" />
            Verificar Stock
          </Button>
        </div>
      </div>

      {/* Resumen Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Costos Pendientes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {resumen?.costos_pendientes?.cantidad || 0}
            </div>
            <p className="text-sm text-gray-500">
              {formatMoney(resumen?.costos_pendientes?.monto_total)}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Alertas Stock</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {resumen?.alertas_stock_activas || 0}
            </div>
            <p className="text-sm text-gray-500">Items bajo mínimo</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Guías Sin Facturar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {resumen?.guias_sin_facturar || 0}
            </div>
            <p className="text-sm text-gray-500">Pendientes de venta</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Procesado Este Mes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {formatMoney(resumen?.costos_procesados_mes)}
            </div>
            <p className="text-sm text-gray-500">{resumen?.vinculaciones_totales || 0} vinculaciones</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="costos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="costos" className="gap-2">
            <DollarSign className="w-4 h-4" />
            Costos Pendientes
            {costosPendientes.length > 0 && (
              <Badge variant="secondary">{costosPendientes.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="stock" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alertas Stock
            {alertasStock.length > 0 && (
              <Badge variant="destructive">{alertasStock.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="guias" className="gap-2">
            <Truck className="w-4 h-4" />
            Guías → Ventas
            {guiasSinFacturar.length > 0 && (
              <Badge variant="secondary">{guiasSinFacturar.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="proveedores" className="gap-2">
            <Link2 className="w-4 h-4" />
            Proveedores
          </TabsTrigger>
        </TabsList>

        {/* Costos Pendientes Tab */}
        <TabsContent value="costos">
          <Card>
            <CardHeader>
              <CardTitle>Costos de Producción Pendientes</CardTitle>
              <CardDescription>
                Movimientos y mermas de producción que requieren procesamiento en Finanzas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {costosPendientes.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No hay costos pendientes. Haz clic en "Sincronizar Costos" para importar.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Proveedor/Servicio</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costosPendientes.map((costo) => (
                      <TableRow key={costo.id}>
                        <TableCell>{new Date(costo.fecha).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant={costo.origen === 'merma' ? 'destructive' : 'outline'}>
                            {costo.origen === 'movimiento_produccion' ? 'Servicio' : 'Merma'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate">{costo.descripcion}</TableCell>
                        <TableCell>
                          {costo.proveedor_produccion_nombre || costo.servicio_nombre || '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatMoney(costo.monto_total)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setCostoSeleccionado(costo);
                                setProcesarDialogOpen(true);
                              }}
                            >
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDescartarCosto(costo.id)}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alertas Stock Tab */}
        <TabsContent value="stock">
          <Card>
            <CardHeader>
              <CardTitle>Alertas de Stock Bajo</CardTitle>
              <CardDescription>
                Items de inventario que están por debajo del stock mínimo
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alertasStock.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No hay alertas activas. El inventario está en niveles adecuados.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Stock Actual</TableHead>
                      <TableHead className="text-right">Stock Mínimo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alertasStock.map((alerta) => (
                      <TableRow key={alerta.id}>
                        <TableCell className="font-medium">{alerta.item_nombre}</TableCell>
                        <TableCell className="text-right text-red-600">{alerta.stock_actual}</TableCell>
                        <TableCell className="text-right">{alerta.stock_minimo}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">Bajo</Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" disabled>
                            Crear O/C
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guías Tab */}
        <TabsContent value="guias">
          <Card>
            <CardHeader>
              <CardTitle>Guías de Remisión Sin Facturar</CardTitle>
              <CardDescription>
                Entregas de producción que pueden convertirse en ventas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {guiasSinFacturar.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No hay guías pendientes de facturar.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Guía</TableHead>
                      <TableHead>Corte</TableHead>
                      <TableHead>Modelo</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {guiasSinFacturar.map((guia) => (
                      <TableRow key={guia.id}>
                        <TableCell className="font-medium">{guia.numero_guia}</TableCell>
                        <TableCell>{guia.n_corte}</TableCell>
                        <TableCell>{guia.modelo_nombre || '-'}</TableCell>
                        <TableCell className="text-right">{guia.cantidad}</TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            onClick={() => {
                              setGuiaSeleccionada(guia);
                              setVentaDialogOpen(true);
                            }}
                          >
                            <Receipt className="w-4 h-4 mr-1" />
                            Crear Venta
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Proveedores Tab */}
        <TabsContent value="proveedores">
          <Card>
            <CardHeader>
              <CardTitle>Vinculación de Proveedores</CardTitle>
              <CardDescription>
                Conecta personas de producción (talleres, servicios externos) con proveedores de finanzas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>¿Para qué sirve?</strong> Al vincular una persona de producción con un proveedor de finanzas, 
                cuando proceses costos de esa persona, automáticamente se asociarán al proveedor correcto para generar facturas o pagos.
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Persona (Producción)</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Proveedor en Finanzas</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {proveedores.map((prov) => (
                    <TableRow key={prov.id}>
                      <TableCell className="font-medium">{prov.nombre}</TableCell>
                      <TableCell>{prov.tipo || '-'}</TableCell>
                      <TableCell>{prov.telefono || '-'}</TableCell>
                      <TableCell>
                        {prov.proveedor_finanzas_nombre ? (
                          <Badge variant="outline" className="text-emerald-600">
                            <Check className="w-3 h-3 mr-1" />
                            {prov.proveedor_finanzas_nombre}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">Sin vincular</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {!prov.proveedor_finanzas_id ? (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setPersonaAVincular(prov);
                                setVincularDialogOpen(true);
                              }}
                            >
                              <Link2 className="w-4 h-4 mr-1" />
                              Vincular
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleCrearProveedorDesdePersona(prov)}
                            >
                              + Crear nuevo
                            </Button>
                          </div>
                        ) : (
                          <Badge variant="secondary">✓ Vinculado</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Procesar Costo */}
      <Dialog open={procesarDialogOpen} onOpenChange={setProcesarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Procesar Costo de Producción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">{costoSeleccionado?.descripcion}</p>
              <p className="text-lg font-bold mt-1">{formatMoney(costoSeleccionado?.monto_total)}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium">¿Cómo procesar?</label>
              <Select value={tipoProceso} onValueChange={setTipoProceso}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="factura">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Crear Factura (CxP)
                    </div>
                  </SelectItem>
                  <SelectItem value="gasto">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Registrar como Gasto
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {tipoProceso === 'factura' && (
              <div>
                <label className="text-sm font-medium">Proveedor</label>
                <Select value={proveedorSeleccionado} onValueChange={setProveedorSeleccionado}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {proveedoresFinanzas.map((prov) => (
                      <SelectItem key={prov.id} value={prov.id.toString()}>
                        {prov.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcesarDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleProcesarCosto}>
              Procesar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Crear Venta */}
      <Dialog open={ventaDialogOpen} onOpenChange={setVentaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Venta desde Guía</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Guía: {guiaSeleccionada?.numero_guia}</p>
              <p className="text-sm">Cantidad: {guiaSeleccionada?.cantidad} unidades</p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Nombre del Cliente</label>
              <Input 
                value={clienteNombre}
                onChange={(e) => setClienteNombre(e.target.value)}
                placeholder="Cliente General"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Monto Total (S/)</label>
              <Input 
                type="number"
                step="0.01"
                value={montoVenta}
                onChange={(e) => setMontoVenta(e.target.value)}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVentaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCrearVenta}>
              Crear Venta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Vincular Proveedor */}
      <Dialog open={vincularDialogOpen} onOpenChange={setVincularDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular Proveedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Persona de Producción:</strong>
              </p>
              <p className="text-lg font-bold text-blue-900">{personaAVincular?.nombre}</p>
              {personaAVincular?.tipo && (
                <p className="text-sm text-blue-600">Tipo: {personaAVincular.tipo}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium">Seleccionar Proveedor de Finanzas</label>
              <Select value={proveedorParaVincular} onValueChange={setProveedorParaVincular}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Elige un proveedor..." />
                </SelectTrigger>
                <SelectContent>
                  {proveedoresFinanzas.filter(p => p.nombre).map((p) => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
              <strong>Resultado:</strong> Cuando proceses costos de "{personaAVincular?.nombre}", 
              se asociarán automáticamente al proveedor seleccionado.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVincularDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleVincularProveedor}>
              <Link2 className="w-4 h-4 mr-2" />
              Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegracionPage;
